// Umami analytics loader + custom event dispatcher.
//
// Self-hosted at analytics.skenmy.com. The website ID is set at build
// time via VITE_UMAMI_WEBSITE_ID. Without it, the script is never
// injected — dev builds and PR previews stay quiet.
//
// Respects Do-Not-Track. The Caddyfile CSP allows
// connect-src/script-src for analytics.skenmy.com.

const ENDPOINT = "https://analytics.skenmy.com/script.js";

declare global {
  interface Window {
    umami?: {
      track: (
        event: string | ((props: Record<string, unknown>) => Record<string, unknown>),
        data?: Record<string, unknown>,
      ) => void;
    };
  }
}

/** Fire-and-forget custom event. Safe to call before the script has
 * loaded — Umami queues. */
export function track(event: string, data?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!window.umami) return;
  try {
    window.umami.track(event, data);
  } catch {
    /* swallow */
  }
}

/** Append the Umami tracker once. Idempotent. */
export function installUmami(): void {
  const id = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;
  if (!id) return;
  if (typeof window === "undefined") return;
  if (window.navigator?.doNotTrack === "1") return;
  if (document.querySelector('script[data-umami]')) return;

  const s = document.createElement("script");
  s.defer = true;
  s.src = ENDPOINT;
  s.setAttribute("data-website-id", id);
  // The subdomain must be listed explicitly. Umami's data-domains is
  // an allowlist, so with only the apex here every event fired from
  // daemon.skenmy.com is dropped on the floor — silently, and it
  // looks exactly like nobody playing.
  s.setAttribute("data-domains", "skenmy.com,daemon.skenmy.com");
  s.setAttribute("data-umami", "1");
  document.head.appendChild(s);

  installUmamiEvents();
}

/** Subscribe to the portos:* / mascot / visitor custom events the rest
 * of the app already dispatches, and re-fire them as Umami events.
 * Keeps tracking confined to this module — no `track()` calls scattered
 * across feature code.
 *
 * Spammy events (every poke / projectile hit) are intentionally
 * skipped; we only forward milestones. */
function installUmamiEvents(): void {
  const on = <T extends Event = CustomEvent>(name: string, fn: (e: T) => void): void => {
    window.addEventListener(name, fn as EventListener);
  };

  // Window-manager: every app open gets a beacon with which app.
  on<CustomEvent<{ app: string }>>("portos:app-open", (e) => {
    track("app_open", { app: String(e.detail?.app ?? "unknown") });
  });
  on<CustomEvent<{ app: string }>>("portos:app-close", (e) => {
    track("app_close", { app: String(e.detail?.app ?? "unknown") });
  });

  // OS actions.
  on("portos:lock", () => track("os_action", { kind: "lock" }));
  on("portos:restart", () => track("os_action", { kind: "restart" }));
  on("portos:shutdown", () => track("os_action", { kind: "shutdown" }));
  on("portos:konami", () => track("easter_egg", { name: "konami" }));
  on("portos:bumper", () => track("easter_egg", { name: "bumper" }));
  on("portos:matrix", () => track("easter_egg", { name: "matrix" }));

  // UI affordances + window-manager beats. These were already being
  // dispatched by the rest of the app; we just hadn't forwarded them.
  on("portos:spotlight", () => track("ui_action", { kind: "spotlight" }));
  on("portos:maximize", () => track("ui_action", { kind: "maximize" }));
  on("portos:theme", () => track("ui_action", { kind: "theme" }));
  on("portos:all-closed", () => track("ui_action", { kind: "all_closed" }));
  on<CustomEvent<{ id?: string }>>("portos:project-open", (e) => {
    track("project_open", { id: String(e.detail?.id ?? "") });
  });

  // v1.1 apps that fire their own events.
  on("portos:magic8-ask", () => track("magic8_ask"));
  on("portos:sketch-save", () => track("sketch_save"));
  on<CustomEvent<{ id?: string }>>("portos:mini-poke", (e) => {
    track("mini_poke", { id: String(e.detail?.id ?? "") });
  });

  // Files opened via Preview.
  on<CustomEvent<{ id?: string; label?: string; kind?: string }>>(
    "portos:file-open",
    (e) => {
      track("file_open", {
        id: String(e.detail?.id ?? ""),
        label: String(e.detail?.label ?? ""),
        kind: String(e.detail?.kind ?? ""),
      });
    },
  );

  // Terminal commands. Logging the literal command is fine — they're
  // all from a small known set (bumper, fight, whoami, …).
  on<CustomEvent<{ cmd: string }>>("portos:term-cmd", (e) => {
    track("term_cmd", { cmd: String(e.detail?.cmd ?? "").slice(0, 40) });
  });

  // Contact form submission.
  on("portos:mail-sent", () => track("mail_sent"));

  // Companion / mascot — every poke, plus milestones.
  on<CustomEvent<{ combo: number; crit: boolean; level: number; attack: string; variant: string }>>(
    "portos:mascot-poke",
    (e) => {
      track("mascot_poke", {
        combo: Number(e.detail?.combo ?? 1),
        crit: !!e.detail?.crit,
        level: Number(e.detail?.level ?? 1),
        attack: String(e.detail?.attack ?? "jab"),
        variant: String(e.detail?.variant ?? "pixel"),
      });
    },
  );
  on<CustomEvent<{ length: number }>>("portos:mascot-combo", (e) => {
    track("mascot_combo", { length: Number(e.detail?.length ?? 0) });
  });
  on<CustomEvent<{ level: number; levelUps: number }>>("portos:mascot-level", (e) => {
    track("mascot_level_up", {
      level: Number(e.detail?.level ?? 1),
      step: Number(e.detail?.levelUps ?? 1),
    });
  });
  on<CustomEvent<{ damage: number; kind: string }>>("visitor:hit", (e) => {
    track("visitor_hit", {
      kind: String(e.detail?.kind ?? "?"),
      damage: Number(e.detail?.damage ?? 0),
    });
  });
  on("boss:summoned", () => track("mascot_boss", { phase: "summoned" }));
  on("boss:dismissed", () => track("mascot_boss", { phase: "dismissed" }));
  on<CustomEvent<{ boss?: boolean }>>("portos:mascot-faint", (e) => {
    track("mascot_faint", { boss: !!e.detail?.boss });
  });
  on("portos:mascot-heal", () => track("mascot_heal"));
  on("portos:mascot-revive", () => track("mascot_revive"));
  on("visitor:down", () => track("visitor_down"));
  on("mascot:reset", () => track("mascot_reset"));

  // Achievements — VAPOUR.unlock dispatches `vapour:unlock` with the
  // achievement's id + title + points (and a `hidden` flag, hidden
  // unlocks still get tracked; the title is the user-visible label).
  on<CustomEvent<{ id: string; title: string; points: number; hidden: boolean }>>(
    "vapour:unlock",
    (e) => {
      track("achievement", {
        id: String(e.detail?.id ?? ""),
        title: String(e.detail?.title ?? ""),
        points: Number(e.detail?.points ?? 0),
        hidden: !!e.detail?.hidden,
      });
    },
  );
}
